import { StowageRuleConsequenceType } from 'src/enums/stowage/stowageRule/stowageRuleConsequenceType';
import { StowageRuleViolationStatus } from 'src/enums/stowage/stowageRule/stowageRuleViolationStatus';
import { StowageRuleConsequenceBM } from 'src/types/BusinessModel/Stowage/StowageRule/StowageRuleConseqenceBM';
import { StowageRuleContext } from 'src/types/ViewModel/Stowage/StowageRule/StowageRuleContext';
import { StowageRulesOutputs } from 'src/types/ViewModel/Stowage/StowageRule/StowageRulesOutputs';
import { Violation } from 'src/types/ViewModel/Stowage/Violation/Violation';
import { getDateNow } from 'odfjell-date-time-utils/dateTimeUtils';
import { UUID } from 'crypto';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { StowageRuleId } from 'src/enums/stowage/stowageRule/stowageRuleId';
import { getIllegalComminglingTextForStowageRuleContext } from './illegalComminglingTextUtils';

export function addViolationWithoutCreatingDuplicates(violations: Violation[], violation: Violation): void {
    const exists = violations.some((v) => v === violation);
    if (!exists) {
        violations.push(violation);
    }
}

export function processConsequence(
    stowageRuleId: number,
    consequence: StowageRuleConsequenceBM,
    stowageRuleContext: StowageRuleContext,
    stowageRulesOutputs: StowageRulesOutputs
): void {
    if (stowageRuleContext.adjacentTanks.length === 0) {
        stowageRuleContext.adjacentTanks = [null];
    }
    stowageRuleContext.adjacentTanks.forEach((adjacentTank) => {
        createAndAddViolation(stowageRuleId, consequence, stowageRuleContext, adjacentTank, stowageRulesOutputs);
    });
}

export function createAndAddViolation(
    stowageRuleId: number,
    consequence: StowageRuleConsequenceBM,
    stowageRuleContext: StowageRuleContext,
    adjacentTank: any | null,
    stowageRulesOutputs: StowageRulesOutputs
): void {
    const cargoKey = (tankId: number) =>
        stowageRuleContext.operationTankContents
            ?.get(tankId)
            ?.cargoContents.filter((content) => content.weight.gt(0))
            .map((content) => content.cargo.cargoGuid)
            .sort()
            .join(',');
    const evaluatedTankIds = adjacentTank ? [stowageRuleContext.tank?.tankId, adjacentTank.tankId] : undefined;
    const ruleEvidence = stowageRuleContext.ruleEvidence?.filter((evidence) => {
        const tankId = JSON.parse(evidence)[1];
        return !evaluatedTankIds || tankId == null || evaluatedTankIds.includes(tankId);
    });
    const newViolation: Violation = {
        ...(ruleEvidence
            ? {
                  ruleEvaluation: {
                      inputs: JSON.stringify([stowageRuleContext.ruleDefinition, Array.from(new Set(ruleEvidence)).sort()]),
                      position: stowageRuleContext.evaluationPosition,
                      key: stowageRuleContext.evaluationKey,
                  },
              }
            : {}),
        ...(stowageRuleContext.operationTankContents
            ? {
                  sequenceConflictKey: `${cargoKey(stowageRuleContext.tank?.tankId) ?? ''}|${cargoKey(adjacentTank?.tankId) ?? ''}`,
              }
            : {}),
        violationGuid: crypto.randomUUID(),
        stowageRuleId: stowageRuleId,
        description: consequence.parameter,
        type: consequence.type,
        status: consequence.type === StowageRuleConsequenceType.SoftRule ? StowageRuleViolationStatus.New : StowageRuleViolationStatus.NotApplicable,
        portCallGuid: stowageRuleContext.portCall?.portCallGuid,
        portCall: stowageRuleContext.portCall,
        berthCallGuid: stowageRuleContext.berthCall?.berthCallGuid,
        berthCall: stowageRuleContext.berthCall,
        cargoGuid: stowageRuleContext.cargo?.cargoGuid,
        cargo: stowageRuleContext.cargo,
        cargoOperationGuid: stowageRuleContext.cargoOperation?.cargoOperationGuid,
        cargoOperation: stowageRuleContext.cargoOperation,
        tankId: stowageRuleContext.tank?.tankId,
        tank: stowageRuleContext.tank,
        adjacentTankId: adjacentTank?.tankId,
        adjacentTank: adjacentTank,
    };

    overrideViolationDescription(newViolation, stowageRuleContext);

    if (consequence) {
        switch (consequence.type) {
            case StowageRuleConsequenceType.HardRule:
                addViolationWithoutCreatingDuplicates(stowageRulesOutputs._hardRuleViolations, newViolation);
                break;
            case StowageRuleConsequenceType.SoftRule:
                addViolationWithoutCreatingDuplicates(stowageRulesOutputs._softRuleViolations, newViolation);
                break;
            case StowageRuleConsequenceType.Warning:
                addViolationWithoutCreatingDuplicates(stowageRulesOutputs._warnings, newViolation);
                break;
        }
    }
}

export const isStowagePlanValid = (violations: Violation[]): boolean => {
    if (!violations) return true;
    return violations.filter((v) => v.type === StowageRuleConsequenceType.HardRule).length === 0;
};

const overrideViolationDescription = (violation: Violation, stowageRuleContext: StowageRuleContext) => {
    if (violation.stowageRuleId === StowageRuleId.IllegalComminglingHardRule) {
        violation.description = getIllegalComminglingTextForStowageRuleContext(violation.description, stowageRuleContext);
    }
};

const getStatusFromViolationList = (violations: any[]): StowageRuleConsequenceType | null => {
    if (violations?.some((o) => o.type === StowageRuleConsequenceType.HardRule)) {
        return StowageRuleConsequenceType.HardRule;
    } else if (violations?.some((o) => o.type === StowageRuleConsequenceType.SoftRule && o.status !== StowageRuleViolationStatus.Accepted)) {
        return StowageRuleConsequenceType.SoftRule;
    }
    return null;
};

export const getCargoPortViolationStatus = (cargoGuid: UUID, portCallGuid: UUID, violations: Violation[]): StowageRuleConsequenceType | null => {
    return getStatusFromViolationList(violations?.filter((v) => v.cargo?.cargoGuid === cargoGuid && v.portCall?.portCallGuid === portCallGuid));
};

export const getCargoBerthViolationStatus = (cargoGuid: UUID, berthCallGuid: UUID, violations: Violation[]): StowageRuleConsequenceType | null => {
    return getStatusFromViolationList(violations?.filter((v) => v.cargo?.cargoGuid === cargoGuid && v.berthCall?.berthCallGuid === berthCallGuid));
};

export const getCargoViolationStatus = (cargoGuid: UUID, violations: Violation[]): StowageRuleConsequenceType | null => {
    return getStatusFromViolationList(violations?.filter((v) => v.cargo?.cargoGuid === cargoGuid));
};

export const getPortCallViolationStatus = (portCallGuid: UUID, violations: Violation[]): StowageRuleConsequenceType | null => {
    return getStatusFromViolationList(violations?.filter((v) => v.portCall?.portCallGuid === portCallGuid));
};

export const getBerthCallViolationStatus = (berthCallGuid: UUID, violations: Violation[]): StowageRuleConsequenceType | null => {
    return getStatusFromViolationList(violations?.filter((v) => v.berthCall?.berthCallGuid === berthCallGuid));
};

export const getViolationType = (violationType: StowageRuleConsequenceType): string => {
    switch (violationType) {
        case StowageRuleConsequenceType.HardRule:
            return 'Hard rule';
        case StowageRuleConsequenceType.SoftRule:
            return 'Soft rule';
        case StowageRuleConsequenceType.Warning:
            return 'Warning';
        default:
            return 'N/A';
    }
};

export const getHardRuleViolationsInTank = (
    violations: Violation[],
    tankId: number,
    portCallGuid: UUID,
    berthCall?: RotationBerthCall
): Violation[] => {
    return (
        violations?.filter(
            (v) =>
                v.tank?.tankId === tankId &&
                v.portCall?.portCallGuid === portCallGuid &&
                (!berthCall || v.berthCall?.berthCallGuid === berthCall.berthCallGuid) &&
                v.type === StowageRuleConsequenceType.HardRule
        ) ?? ([] as Violation[])
    );
};

export const getSoftRuleViolationsInTank = (
    violations: Violation[],
    tankId: number,
    portCallGuid: UUID,
    berthCall?: RotationBerthCall
): Violation[] => {
    return (
        violations?.filter(
            (v) =>
                v.tank?.tankId === tankId &&
                v.portCall?.portCallGuid === portCallGuid &&
                (!berthCall || v.berthCall?.berthCallGuid === berthCall.berthCallGuid) &&
                v.type === StowageRuleConsequenceType.SoftRule &&
                v.status !== StowageRuleViolationStatus.Accepted
        ) ?? ([] as Violation[])
    );
};

export const acceptSoftRuleViolations = (violations: Violation[], user: string): void => {
    violations.forEach((v) => {
        v.statusChangedBy = user;
        v.statusChangedUtc = getDateNow();
        v.status = StowageRuleViolationStatus.Accepted;
    });
};

export const getViolationId = (violation: Violation): string => {
    const id = `${violation.stowageRuleId}-${violation.type}-${violation.portCall?.portCallGuid ?? violation.portCallGuid}-${
        violation.berthCall?.berthCallGuid ?? violation.berthCallGuid
    }-${violation.cargo?.cargoGuid ?? violation.cargoGuid}-${violation.cargoOperation?.cargoOperationGuid ?? violation.cargoOperationGuid}-${
        violation.tank?.tankId ?? violation.tankId
    }-${violation.adjacentTank?.tankId ?? violation.adjacentTankId}`;
    return violation.sequenceConflictKey ? `${id}-${violation.sequenceConflictKey}` : id;
};

export const updateViolations = (oldViolations: Violation[], newViolations: Violation[]) => {
    const oldViolationsMap = new Map<string, Violation[]>();
    oldViolations.forEach((v) => {
        const id = getViolationId(v);
        const matches = oldViolationsMap.get(id) ?? [];
        matches.push(v);
        oldViolationsMap.set(id, matches);
    });

    const updatedViolations: Violation[] = [];

    newViolations.forEach((v) => {
        const violationId = getViolationId(v);
        const matches = oldViolationsMap.get(violationId);
        if (matches?.length) {
            const existingViolation = matches.shift();
            const updatedViolation: Violation = {
                ...v,
                violationGuid: existingViolation.violationGuid,
                status: existingViolation.status,
                statusChangedBy: existingViolation.statusChangedBy,
                statusChangedUtc: existingViolation.statusChangedUtc,
                isNewViolation: false,
            };
            updatedViolations.push(updatedViolation);
        } else {
            v.isNewViolation = true;
            updatedViolations.push(v);
        }
    });

    const deletedViolations = Array.from(oldViolationsMap.values()).flat();

    return { updatedViolations, newViolations: updatedViolations.filter((v) => v.isNewViolation), deletedViolations };
};

export const getAddedViolations = (violations1: Violation[], violations2: Violation[]): Violation[] => {
    return violations2.filter((v2) => !violations1.some((v1) => getViolationId(v1) === getViolationId(v2)));
};
